/**
 * Home Page Component
 *
 * Main landing page after user sign-in. Displays tenant info header
 * and placeholder for future transaction index.
 *
 * @fileoverview Home page with two-stripe layout
 *
 * @example
 * // In router
 * <Route path="/home" element={<Home />} />
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Text,
  Badge,
  Spinner,
  Center,
  Flex,
  Button,
  HStack,
} from '@chakra-ui/react';
import { MdSettings } from 'react-icons/md';
import { TransactionIndex } from '../components/transactions';
import { useUser } from '../Context/userContext';
import { useAuth } from '../hooks/useAuth';

/**
 * Loading state component
 */
const LoadingState: React.FC = () => (
  <Center h="100vh" bg="bg.primary">
    <VStack spacing={4}>
      <Spinner size="xl" color="accent.primary" thickness="4px" />
      <Text color="text.secondary">Loading...</Text>
    </VStack>
  </Center>
);

/**
 * Compute aggregate status from all Plaid tokens.
 * "Connected" only if ALL tokens are ACTIVE — no false positives.
 */
const getAggregateStatus = (tokens: Array<{ status: string }>): { color: string; text: string } => {
  if (tokens.length === 0) {
    return { color: 'gray', text: 'Not Connected' };
  }

  const hasRevoked = tokens.some((t) => t.status === 'REVOKED');
  const hasLoginRequired = tokens.some((t) => t.status === 'LOGIN_REQUIRED');

  if (hasRevoked) {
    return { color: 'red', text: 'Revoked' };
  }
  if (hasLoginRequired) {
    return { color: 'yellow', text: 'Login Required' };
  }

  // All tokens are ACTIVE
  return { color: 'green', text: 'Connected' };
};

/**
 * Home content component
 */
const HomeContent: React.FC = () => {
  const navigate = useNavigate();
  const { selectedTenant, plaidTokens, isLoading, error } = useUser();

  // Show loading while user data loads
  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <VStack spacing={0} align="stretch" h="full">
      {/* Upper Stripe - Tenant Info Header */}
      <Flex
        h="56px"
        px={6}
        bg="bg.surface"
        borderBottomWidth="1px"
        borderBottomColor="xacto.borderGray"
        align="center"
        justify="space-between"
        flexShrink={0}
        position="sticky"
        top="64px"
        zIndex={15}
      >
        {/* Left: Tenant Name */}
        <Text
          color="text.primary"
          fontWeight="semibold"
          fontSize="lg"
        >
          {selectedTenant?.name || 'No Tenant Selected'}
        </Text>

        {/* Right: Plaid Connection Status + Status Page Button */}
        <HStack spacing={3}>
          <Button
            leftIcon={<MdSettings />}
            size="sm"
            variant="outline"
            colorScheme="cyan"
            color="accent.primary"
            borderColor="xacto.electricCyan"
            onClick={() => navigate('/status')}
            _hover={{ bg: 'xacto.mutedNavy' }}
          >
            Status
          </Button>
          <Badge
            colorScheme={getAggregateStatus(plaidTokens).color}
            px={3}
            py={1}
            borderRadius="md"
            fontSize="sm"
          >
            {getAggregateStatus(plaidTokens).text}
          </Badge>
        </HStack>
      </Flex>

      {/* Error display */}
      {error && (
        <Box
          bg="red.900"
          borderColor="red.500"
          borderWidth="1px"
          borderRadius="md"
          p={4}
          m={4}
        >
          <Text color="red.200">{error}</Text>
        </Box>
      )}

      {/* Bottom Stripe - Transaction Index */}
      <Box flex="1" p={6} overflow="clip">
        <TransactionIndex />
      </Box>
    </VStack>
  );
};

/**
 * Home Page
 *
 * Main landing page after authentication featuring:
 * - Upper stripe with tenant name and Plaid connection status
 * - Bottom stripe placeholder for future transaction index
 *
 * Requires authentication - redirects to home if not authenticated.
 *
 * @returns Home page
 */
export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && (!user || !session)) {
      navigate('/');
    }
  }, [authLoading, user, session, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return <LoadingState />;
  }

  // Don't render if not authenticated
  if (!user || !session) {
    return null;
  }

  // HomeContent uses UserProvider from AppRouter context
  return <HomeContent />;
};

export default Home;
